import { useRef, useLayoutEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import Button from "../button";

const getScrollableAncestors = (el) => {
    const ancestors = [];
    let node = el?.parentElement;
    while (node && node !== document.body) {
        const { overflow, overflowX, overflowY } = window.getComputedStyle(node);
        const ox = `${overflow} ${overflowX} ${overflowY}`;
        if (/(auto|scroll|overlay)/.test(ox)) {
            ancestors.push(node);
        }
        node = node.parentElement;
    }
    return ancestors;
};

const ExternalTooltip = ({
    title,
    description,
    confirm,
    setOnClickConfirm,
    children,
    pointerPlacement = "bottom",
    triggerClassName = "relative inline-flex",
}) => {
    const triggerRef = useRef(null);
    const [coords, setCoords] = useState(null);

    const updatePosition = useCallback(() => {
        const el = triggerRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        if (pointerPlacement === "left") {
            setCoords({
                left: rect.right,
                top: rect.top + rect.height / 2,
                placement: "left",
            });
        } else {
            setCoords({
                left: rect.left + rect.width / 2,
                top: rect.bottom,
                placement: "bottom",
            });
        }
    }, [pointerPlacement]);

    useLayoutEffect(() => {
        if (confirm) {
            setCoords(null);
            return undefined;
        }

        const el = triggerRef.current;
        if (!el) return undefined;

        updatePosition();

        const scrollables = getScrollableAncestors(el);
        scrollables.forEach((node) => node.addEventListener("scroll", updatePosition, true));
        window.addEventListener("scroll", updatePosition, true);
        window.addEventListener("resize", updatePosition);

        return () => {
            scrollables.forEach((node) => node.removeEventListener("scroll", updatePosition, true));
            window.removeEventListener("scroll", updatePosition, true);
            window.removeEventListener("resize", updatePosition);
        };
    }, [confirm, updatePosition, children, pointerPlacement]);

    const handleConfirm = () => {
        setOnClickConfirm(true);
    };

    const isLeft = coords?.placement === "left";

    const tooltip =
        !confirm &&
        coords &&
        typeof document !== "undefined" &&
        createPortal(
            isLeft ? (
                <div
                    className="pointer-events-auto z-[100] fixed flex flex-row items-center text-white transition duration-150 ease-in-out"
                    style={{
                        left: coords.left + 8,
                        top: coords.top,
                        transform: "translateY(-50%)",
                    }}
                    role="dialog"
                    aria-label={title}
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    <div
                        className="h-0 w-0 shrink-0 border-y-8 border-y-transparent border-r-[12px] border-r-blue-500"
                        aria-hidden
                    />
                    <div className="w-[min(319px,calc(100vw-3rem))] bg-blue-500 rounded shadow-lg p-4 flex flex-col gap-3 rounded-l-none sm:rounded-l-lg">
                        <div className="text-lg">{title}</div>
                        <div className="text-sm">{description}</div>
                        <div className="flex justify-end">
                            <Button
                                color={"BLUE"}
                                size={"SM"}
                                onClick={() => handleConfirm()}
                            >
                                D'accord
                            </Button>
                        </div>
                    </div>
                </div>
            ) : (
                <div
                    className="pointer-events-auto z-[100] w-[319px] fixed transition duration-150 ease-in-out text-white"
                    style={{
                        left: coords.left,
                        top: coords.top + 8,
                        transform: "translateX(-50%)",
                    }}
                    role="dialog"
                    aria-label={title}
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    <div className="h-0 w-0 border-x-8 border-x-transparent border-b-[12px] border-b-blue-500 mx-auto" />
                    <div
                        className={
                            "bg-blue-500 rounded shadow-lg p-4 flex flex-col gap-3"
                        }
                    >
                        <div className="text-lg">{title}</div>
                        <div className="text-sm">{description}</div>
                        <div className="flex justify-end">
                            <Button
                                color={"BLUE"}
                                size={"SM"}
                                onClick={() => handleConfirm()}
                            >
                                D'accord
                            </Button>
                        </div>
                    </div>
                </div>
            ),
            document.body
        );

    return (
        <>
            <span ref={triggerRef} className={triggerClassName}>
                {children}
            </span>
            {tooltip}
        </>
    );
};

export default ExternalTooltip;
